import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { forwardRef } from 'react'
import tw, { styled } from 'twin.macro'

import type {
  ScrollAreaProps,
  ScrollAreaScrollbarProps,
  ScrollAreaViewportProps,
} from './types'

const StyledRoot = styled(ScrollAreaPrimitive.Root)`
  ${tw`flex overflow-hidden`}
`

const StyledViewport = styled(ScrollAreaPrimitive.Viewport)`
  // this z-index 0 guarantees that the viewport will be below the scrollbars
  // which are set to z-index 1
  ${tw`z-[0] flex flex-grow`}
  border-radius: inherit;
`

const StyledScrollbar = styled(ScrollAreaPrimitive.Scrollbar)`
  ${tw`
    flex
    p-0.5
    bg-default-neutral hover:bg-default-emphasis
    select-none
    transition-colors
    z-[1]
  `}
  touch-action: none;

  ${StyledRoot}:not([data-visible-track='true']) & {
    display: none;
  }

  &[data-orientation='vertical'] {
    ${tw`w-2.5`}
  }

  &[data-orientation='horizontal'] {
    ${tw`flex-col h-2.5`}
  }
`

const StyledThumb = styled(ScrollAreaPrimitive.Thumb)`
  ${tw`
    flex-1
    bg-constant-dark-40
    rounded-[10px]
  `}

  &::before {
    content: '';
    ${tw`
      absolute top-1/2 left-1/2
      transform -translate-x-1/2 -translate-y-1/2
      w-full h-full min-w-[20px] min-h-[20px]
    `}
  }
`

export function ScrollArea({
  children,
  invisible = false,
  ...rest
}: ScrollAreaProps) {
  return (
    <StyledRoot {...rest} data-visible-track={!invisible}>
      {children}
    </StyledRoot>
  )
}

ScrollArea.Viewport = forwardRef<HTMLDivElement, ScrollAreaViewportProps>(
  function ScrollAreaViewport({ children, ...rest }, ref) {
    return (
      <StyledViewport ref={ref} {...rest}>
        {children}
      </StyledViewport>
    )
  },
)

ScrollArea.HorizontalScrollbar = function HorizontalScrollbar({
  ...rest
}: ScrollAreaScrollbarProps) {
  return (
    <StyledScrollbar orientation="horizontal" {...rest}>
      <StyledThumb />
    </StyledScrollbar>
  )
}

ScrollArea.VerticalScrollbar = function VerticalScrollbar({
  ...rest
}: ScrollAreaScrollbarProps) {
  return (
    <StyledScrollbar orientation="vertical" {...rest}>
      <StyledThumb />
    </StyledScrollbar>
  )
}
